import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';

const styles = theme => ({
    table: {
        display: 'table',
        width: '100%',
        borderCollapse: 'collapse',
        borderSpacing: 0,
        '& td': {
            textAlign: 'center',
            height: '48px',
        },
        '& thead td': {
            fontWeight: 'normal',
            color: theme.palette.text.secondary,
        },
        '& tbody td': {
            verticalAlign: 'inherit',
        },
        '& tbody tr:last-child td': {
            borderBottom: 'none',
        },
        '& tfoot td': {
            color: theme.palette.text.primary,
            borderBottom: 'none',
        },
        '& td.left': {
            textAlign: 'left',
        },
        '& td.right': {
            textAlign: 'right',
        },
        '& tr td:first-child': {
            paddingLeft: '24px',
        },
        '& tr td:last-child': {
            paddingRight: '24px',
        },
    },
    borders: {
        '& tfoot td': {
            borderTop: '1px solid #ddd',
        },
        '& td': {
            borderBottom: '1px solid #ddd',
        },
    },
    compact: {
        '& td': {
            height: '28px',
        },
        '& tr td:first-child': {
            paddingLeft: 0,
        },
        '& tr td:last-child': {
            paddingRight: 0,
        },
    },
});

function cellRenderer(data) {
    return cell => {
        if (cell !== null) {
            if (typeof cell === 'string') {
                return cell;
            }
            if (typeof cell === 'function') {
                return cell(data);
            }
            if (cell) {
                return cell;
            }
        }
        return '';
    };
}

function findColSpan({ columns, header }, data) {
    if (columns && header && columns.length !== header.length) {
        return Math.max(columns.length, header.length);
    }
    if (columns) {
        return columns.reduce(
            (sum, column) => sum + (column && column.hidden ? 0 : 1),
            0
        );
    }
    if (header) {
        return header.length;
    }
    if (data && data.length) {
        return data[0].length;
    }
    return 0;
}

function createFormater(columns) {
    if (!columns || !columns.length) {
        return {
            formatCell(col, value) {
                return value;
            },
            getCellClassName() {
                return '';
            },
            getCellStyle() {
                return {};
            },
            isHidden() {
                return false;
            },
        };
    }
    return {
        formatCell(col, value, row) {
            const column = columns[col];
            if (column && typeof column.format === 'function') {
                return column.format(value, row);
            }
            return value;
        },
        getCellClassName(col) {
            const buff = [];
            const column = columns[col];
            if (column && column.styles) {
                const styles = column.styles;
                if (styles.align) {
                    buff.push(styles.align);
                }
            }
            return buff.join(' ');
        },
        getCellStyle(col) {
            const buff = {};
            const column = columns[col];
            if (column && column.styles) {
                const styles = column.styles;
                if (styles.width) {
                    buff.width = styles.width;
                }
            }
            return buff;
        },
        isHidden(col) {
            if (columns[col]) {
                return !!columns[col].hidden;
            }
            return false;
        },
    };
}

function Header({ options, data }) {
    const { header, columns } = options;
    if (Array.isArray(header) && header.length) {
        const renderCell = cellRenderer(data);
        const formater = createFormater(columns);
        return (
            <thead>
                <tr>
                    {header.map((cell, i) =>
                        formater.isHidden(i) ? null : (
                            <td
                                key={i}
                                style={formater.getCellStyle(i)}
                                className={formater.getCellClassName(i)}
                            >
                                {renderCell(cell)}
                            </td>
                        )
                    )}
                </tr>
            </thead>
        );
    }
    return null;
}

function Footer({ options, data }) {
    const { footer, columns } = options;
    if (Array.isArray(footer) && footer.length) {
        const formater = createFormater(columns);
        const renderCell = cellRenderer(data);
        return (
            <tfoot>
                <tr>
                    {footer.map((cell, i) =>
                        formater.isHidden(i) ? null : (
                            <td
                                key={i}
                                style={formater.getCellStyle(i)}
                                className={formater.getCellClassName(i)}
                            >
                                {renderCell(cell)}
                            </td>
                        )
                    )}
                </tr>
            </tfoot>
        );
    }
    return null;
}

function Body({ options, data }) {
    const { columns } = options;
    const renderCell = cellRenderer(data);
    const formater = createFormater(columns);
    return (
        <tbody>
            {data.length ? (
                data.map((row, i) => (
                    <tr key={i}>
                        {row.map((cell, i) =>
                            formater.isHidden(i) ? null : (
                                <td
                                    key={i}
                                    style={formater.getCellStyle(i)}
                                    className={formater.getCellClassName(i)}
                                >
                                    {formater.formatCell(
                                        i,
                                        renderCell(cell),
                                        row
                                    )}
                                </td>
                            )
                        )}
                    </tr>
                ))
            ) : (
                <tr>
                    <td className="left" colSpan={findColSpan(options)}>
                        {options.empty || 'No data'}
                    </td>
                </tr>
            )}
        </tbody>
    );
}

function SimpleTable({ classes, data, options, className }) {
    const { compact, borders } = options;
    const rootClassName = classNames(
        classes.table,
        {
            [classes.borders]: borders === undefined || borders,
            [classes.compact]: compact,
        },
        className
    );
    return (
        <Typography className={rootClassName} component="table">
            <Header options={options} data={data} />
            <Body options={options} data={data} />
            <Footer options={options} data={data} />
        </Typography>
    );
}

SimpleTable.propTypes = {
    classes: PropTypes.object.isRequired,
    data: PropTypes.array.isRequired,
    options: PropTypes.PropTypes.shape({
        header: PropTypes.array,
        footer: PropTypes.array,
        columns: PropTypes.array,
        compact: PropTypes.bool,
        borders: PropTypes.bool,
        empty: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    }),
    className: PropTypes.string,
};

SimpleTable.defaultProps = {
    options: {
        header: [],
        foooter: [],
        columns: [],
        compact: false,
        borders: true,
        empty: 'No data',
    },
    className: '',
};
export default withStyles(styles)(SimpleTable);
